'use client';

import {
  Flex,
  Button,
  ButtonGroup,
  Text,
  Dropdown,
  ResultListItem,
  RestoreDocumentDialog,
} from '@/_components';
import { type FC, useCallback, useState, useMemo, useContext } from 'react';
import { DocumentBulkActionBarProps } from './DocumentBulkActionBar.types';
import styles from './DocumentBulkActionBar.module.css';
import { DeleteMultipleItemsDialog } from './_components/DeleteMultipleItemsDialog';
import { DownloadMultipleDocumentsDialog } from './_components/DownloadMultipleDocumentsDialog';
import { ClientDownloadMultipleDocumentsDialog } from './_components/ClientDownloadMultipleDocumentsDialog';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';

export const DocumentBulkActionBar: FC<DocumentBulkActionBarProps> = ({
  companyId,
  currentPath,
  selectedDocumentItems,
  shouldDisableClientActions,
  clearSelections,
  refresh,
  onItemsCopy,
  onItemsMove,
}) => {
  const { permissions, isAdvisor } = useContext(UserContext);
  const { preferences } = useContext(FirmSettingsContext);
  const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);
  const [deleteDialogOpened, setDeleteDialogOpened] = useState(false);
  const [downloadDialogOpened, setDownloadDialogOpened] = useState(false);
  const [clientDownloadDialogOpened, setClientDownloadDialogOpened] =
    useState(false);

  const moveSelected = useCallback(() => {
    onItemsMove(selectedDocumentItems);
  }, [onItemsMove, selectedDocumentItems]);

  const copySelected = useCallback(() => {
    onItemsCopy(selectedDocumentItems);
  }, [onItemsCopy, selectedDocumentItems]);

  const disableFileEditAction =
    !isAdvisor && preferences?.disable_file_edit_action;

  const deleteSelected = useCallback(() => {
    setDeleteDialogOpened(true);
  }, []);

  const downloadSelected = useCallback(() => {
    if (isAdvisor) {
      setDownloadDialogOpened(true);
    } else {
      setClientDownloadDialogOpened(true);
    }
  }, [isAdvisor]);

  const allItemsDeleted = useMemo(() => {
    return selectedDocumentItems.every((item) => item.deleted_at);
  }, [selectedDocumentItems]);

  const hasMixedDeletedState = useMemo(() => {
    const hasDeleted = selectedDocumentItems.some((item) => item.deleted_at);
    const hasNotDeleted = selectedDocumentItems.some(
      (item) => !item.deleted_at,
    );
    return hasDeleted && hasNotDeleted;
  }, [selectedDocumentItems]);

  const hasMoveDocumentPermission: boolean = getHasPermission(
    PermissionsMapEnum['document.move'],
    permissions,
    companyId,
  );

  const hasCopyDocumentPermission: boolean = getHasPermission(
    PermissionsMapEnum['document.copy'],
    permissions,
    companyId,
  );

  const hasDeleteDocumentPermission: boolean = getHasPermission(
    PermissionsMapEnum['document.delete'],
    permissions,
    companyId,
  );

  const actions: ResultListItem[] = useMemo(() => {
    if (allItemsDeleted) {
      return [
        {
          name: 'Restore Documents',
          onClick: () => setIsRestoreDialogOpen(true),
        },
      ];
    }

    if (shouldDisableClientActions || disableFileEditAction) {
      return [
        {
          name: 'Download Selected',
          onClick: downloadSelected,
        },
      ];
    }

    const _actions: ResultListItem[] = [
      {
        name: 'Move Selected',
        onClick: moveSelected,
        isDisabled: !hasMoveDocumentPermission,
      },
      {
        name: 'Copy Selected',
        onClick: copySelected,
        isDisabled: !hasCopyDocumentPermission,
      },
      {
        name: 'Delete Selected',
        onClick: deleteSelected,
        isDisabled: !hasDeleteDocumentPermission,
      },
      {
        name: 'Download Selected',
        onClick: downloadSelected,
      },
    ];

    return _actions;
  }, [
    hasDeleteDocumentPermission,
    hasCopyDocumentPermission,
    hasMoveDocumentPermission,
    allItemsDeleted,
    copySelected,
    deleteSelected,
    downloadSelected,
    moveSelected,
    shouldDisableClientActions,
    disableFileEditAction,
  ]);

  const onDeleted = useCallback(async () => {
    await refresh();
    setDeleteDialogOpened(false);
  }, [refresh]);

  return (
    <>
      <DeleteMultipleItemsDialog
        companyId={companyId}
        currentPath={currentPath}
        selectedDocumentItems={selectedDocumentItems}
        isOpen={deleteDialogOpened}
        onClose={() => setDeleteDialogOpened(false)}
        onDeleted={onDeleted}
      />

      <DownloadMultipleDocumentsDialog
        companyId={companyId}
        isOpen={downloadDialogOpened}
        documents={selectedDocumentItems}
        onClose={() => setDownloadDialogOpened(false)}
        onDownloaded={clearSelections}
      />

      <ClientDownloadMultipleDocumentsDialog
        companyId={companyId}
        isOpen={clientDownloadDialogOpened}
        documents={selectedDocumentItems}
        onClose={() => setClientDownloadDialogOpened(false)}
        onDownloaded={clearSelections}
      />

      <RestoreDocumentDialog
        isOpen={isRestoreDialogOpen}
        onClose={() => setIsRestoreDialogOpen(false)}
        selectedDocuments={selectedDocumentItems}
      />

      <Flex className={styles.outerContainer}>
        <Flex
          padding={12}
          gap={12}
          isColumn={true}
          isFullWidth
          className={styles.innerContainer}
        >
          <Flex paddingY={4} isYCentered={true} gap={4} isBetween>
            <Text>
              {selectedDocumentItems.length}{' '}
              {selectedDocumentItems.length === 1 ? 'item' : 'items'} selected.
            </Text>

            <ButtonGroup isSameLine={true}>
              <Button
                size="small"
                value="Clear Selections"
                intent="secondary"
                onClick={clearSelections}
              />

              <Dropdown items={actions}>
                <Button
                  size="small"
                  value="Bulk Action"
                  intent="primary"
                  iconRight="caret-down"
                  isDisabled={hasMixedDeletedState}
                  tooltip={
                    hasMixedDeletedState
                      ? 'Not available for a mix of deleted and non-deleted documents'
                      : undefined
                  }
                  withTooltip={hasMixedDeletedState}
                  tooltipPlacement="bottom"
                />
              </Dropdown>
            </ButtonGroup>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};
